/**
 * Custom hook to handle responsive design breakpoints and orientation checks using Material-UI.
 *
 * @returns {Object} An object containing various responsive utilities:
 * - `down(breakpoint: Breakpoint)`: Checks if the screen width is below the specified breakpoint.
 * - `up(breakpoint: Breakpoint)`: Checks if the screen width is above the specified breakpoint.
 * - `between(start: Breakpoint, end: Breakpoint)`: Checks if the screen width is between the specified breakpoints.
 * - `only(breakpoint: Breakpoint)`: Checks if the screen width matches the specified breakpoint.
 * - `isMobile`: Boolean indicating if the screen width is below the "sm" breakpoint.
 * - `isTablet`: Boolean indicating if the screen width is between the "sm" and "md" breakpoints.
 * - `isTabletUp`: Boolean indicating if the screen width is above the "sm" breakpoint.
 * - `isDesktop`: Boolean indicating if the screen width is above the "md" breakpoint.
 * - `isLargeScreen`: Boolean indicating if the screen width is above the "lg" breakpoint.
 * - `currentBreakpoint`: The current breakpoint as a string ("xs", "sm", "md", "lg", "xl").
 * - `width`: The current screen width in pixels.
 * - `height`: The current screen height in pixels.
 * - `isPortrait`: Boolean indicating if the screen orientation is portrait.
 * - `isLandscape`: Boolean indicating if the screen orientation is landscape.
 */
import { Breakpoint, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

const useResponsive = () => {
    const theme = useTheme();

    // Default breakpoints in Material-UI:

    // xs, extra-small: 475px
    // sm, small: 640px
    // md, medium: 768px
    // lg, large: 1024px
    // xl, extra-large: 1280px

    // Breakpoint checks
    const isXs = useMediaQuery(theme.breakpoints.only("xs"));
    const isSm = useMediaQuery(theme.breakpoints.only("sm"));
    const isMd = useMediaQuery(theme.breakpoints.only("md"));
    const isLg = useMediaQuery(theme.breakpoints.only("lg"));
    const isXl = useMediaQuery(theme.breakpoints.up("xl"));

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isTabletUp = useMediaQuery(theme.breakpoints.up("sm"));
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));

    const isPortrait = useMediaQuery("(orientation: portrait)");
    const isLandscape = useMediaQuery("(orientation: landscape)");

    // Determine the current breakpoint
    const currentBreakpoint = isXs ? "xs" : isSm ? "sm" : isMd ? "md" : isLg ? "lg" : "xl";

    return {
        down: (breakpoint: Breakpoint) => useMediaQuery(theme.breakpoints.down(breakpoint)),
        up: (breakpoint: Breakpoint) => useMediaQuery(theme.breakpoints.up(breakpoint)),
        between: (start: Breakpoint, end: Breakpoint) =>
            useMediaQuery(theme.breakpoints.between(start, end)),
        only: (breakpoint: Breakpoint) => useMediaQuery(theme.breakpoints.only(breakpoint)),

        // Basic breakpoint checks
        isMobile,
        isTablet,
        isTabletUp,
        isDesktop,
        isLargeScreen,

        // Get exact current breakpoint
        currentBreakpoint,

        // Get screen width and height in pixels
        width: typeof window !== "undefined" ? window.innerWidth : 0,
        height: typeof window !== "undefined" ? window.innerHeight : 0,

        // Check orientation
        isPortrait,
        isLandscape,
    };
};

export default useResponsive;
